// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  Column,
  Illustration,
  LgPadding,
  MdSpacing,
  PressableAltoIcon,
  Row,
  useScreenSize,
} from '@alto/design-system';
import { type AppPromoCardType } from '@alto/scriptdash/alto/patients/engagement/types/v1/app_promo';
import React from 'react';
import { Linking } from 'react-native';
import styled from 'styled-components/native';
import { useAnalytics } from '~shared/hooks';
import { getPromoCardBackgroundColor } from './utils';

type Props = {
  id: number;
  title: string;
  cardType?: AppPromoCardType | null;
  content?: string;
  ctaText?: string | null;
  imageURL?: string | null;
  onClose?: () => void;
  routeURL?: string | null;
};

const LeftContent = styled(Column)<{ hasImage: boolean }>`
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  ${({ hasImage }) => hasImage && 'max-width: 70%;'}
`;

export const PromoCard = ({ id, title, cardType, content, ctaText = 'Done', imageURL, onClose, routeURL }: Props) => {
  const { isMDScreenOrBigger } = useScreenSize();
  const { trackEvent } = useAnalytics();
  const backgroundColor = getPromoCardBackgroundColor(cardType || 'primary_lighter');
  const imageSource = imageURL?.trim() ? imageURL : null;
  const hasImage = imageSource !== null;

  const handlePress = () => {
    if (routeURL) {
      trackEvent({
        event: 'Promo CTA Clicked',
        params: {
          promoID: id,
          promoTitle: title,
          url: routeURL,
        },
      });
      Linking.openURL(routeURL);
    }
  };

  return (
    <Card
      title={title}
      style={{ backgroundColor }}
      wrapHeader={false}
      RightContent={
        onClose ? (
          <PressableAltoIcon
            name="close"
            onPress={onClose}
            accessibilityLabel={'close promo card'}
            testID={'close-promo-card'}
          />
        ) : undefined
      }
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <Row spaceBetween>
          <LeftContent
            hasImage={hasImage}
            spaceBetween
            testID="promo-card-content"
          >
            {content ? <Body>{content}</Body> : null}
            <MdSpacing />
            {ctaText && routeURL ? (
              <>
                <Button
                  type="primary"
                  onPress={handlePress}
                  label={ctaText}
                  width="inline"
                  small
                />
              </>
            ) : null}
          </LeftContent>
          {imageSource ? (
            <Column>
              <Illustration
                source={imageSource}
                size={isMDScreenOrBigger ? 'xl' : 'lg'}
                accessibilityLabel="promo card"
              />
            </Column>
          ) : null}
        </Row>
      </LgPadding>
    </Card>
  );
};
